import React, { useState } from 'react';
import { Save, XCircle, XOctagon } from 'react-feather';
import { api, Question } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  question: Question;
  onClose: () => void;
  onUpdate: (updated: Question) => void;
}

const EditQuestionModal: React.FC<Props> = ({ question, onClose, onUpdate }) => {
  const { showSnackbar } = useSnackbar();
  const [title, setTitle] = useState(question.title);
  const [description, setDescription] = useState(question.description || '');
  const [isPublic, setIsPublic] = useState(question.is_public);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      showSnackbar('Title is required.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      
      const updated = await api.updateQuestion(question.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        is_public: isPublic,
      });
      
      onUpdate(updated);
      showSnackbar('Question updated.', { variant: 'success' });
      onClose();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to update question', { variant: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Question</h2>
          <button className="btn-close" onClick={onClose}><XCircle size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="title">Question Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span>Public question</span>
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={submitting}
            >
              <XOctagon size={14} />
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              <Save size={14} />
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default EditQuestionModal;
